import { Alert, Typography } from 'antd'
import { FileUploader, useLinkedDocuments } from 'features/files'
import { DocumentAttachments } from 'features/libra/document/DocumentAttachments'
import { useDocumentData } from 'features/libra/document/DocumentDataContext'
import { useLibraDocument } from 'features/libra/document/LibraDocumentContext'
import {
  getFieldSource,
  unwrapFieldValue,
  wrapFieldValue,
} from 'features/libra/ontology/ontologyFieldDefaults'
import { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { useRolodexEntries } from 'features/rolodex'
import { Dispatch, SetStateAction, useMemo, useState } from 'react'
import { RolodexEntryType } from 'types'

import {
  documentAttachmentIdsSchema,
  DocumentAttachmentsSection,
  mergeDocumentAttachmentIds,
} from './documentAttachmentsTypes'

export const DocumentAttachmentsSectionView = ({
  config,
  fieldValues,
  setFieldValues,
}: {
  config: DocumentAttachmentsSection
  fieldValues: FieldValues
  setFieldValues: Dispatch<SetStateAction<FieldValues>> | undefined
}) => {
  const { ticketId, ticketTags } = useTicketAccess()
  const { setSelectedDocumentId } = useLibraDocument()
  const { forceRefresh: refreshNavigation } = useDocumentData()
  const unitId = ticketTags.unit
  const queryTags = useMemo(
    () => (unitId ? { unit: unitId } : undefined),
    [unitId]
  )
  const { documents, isLoading, forceRefresh } = useLinkedDocuments(queryTags)
  const unitQuery = useMemo(
    () => (unitId ? { ids: [unitId], type: RolodexEntryType.UNIT } : undefined),
    [unitId]
  )
  const { units } = useRolodexEntries(unitQuery)
  const [error, setError] = useState<string>()
  const selectedIds =
    documentAttachmentIdsSchema.safeParse(
      unwrapFieldValue(fieldValues[config.field])
    ).data ?? []
  const update = (resolve: (previous: string[]) => string[]) =>
    setFieldValues?.((previous) => ({
      ...previous,
      [config.field]: wrapFieldValue(
        resolve(
          documentAttachmentIdsSchema.safeParse(
            unwrapFieldValue(previous[config.field])
          ).data ?? []
        ),
        getFieldSource(previous[config.field])
      ),
    }))
  const onUploaded = (ids: readonly string[]) => {
    update((previous) => mergeDocumentAttachmentIds(previous, ids))
    setError(undefined)
    void Promise.all([forceRefresh(), refreshNavigation()]).catch(() =>
      setError(
        'Files uploaded, but the document list could not refresh. Save your changes and reload the page.'
      )
    )
  }
  const unit = units[0]
  return (
    <>
      {error && <Alert type='error' title={error} />}
      <DocumentAttachments
        label={config.title}
        documents={documents.map((document) => ({
          id: document.entry_id,
          name: document.visible_name || document.filename || document.entry_id,
        }))}
        selectedIds={selectedIds}
        loading={isLoading}
        readonly={!setFieldValues}
        onChange={(ids) => update(() => ids)}
        onOpen={setSelectedDocumentId}
        uploader={
          unit ? (
            <>
              <Typography.Paragraph type='secondary'>
                Upload files here or select existing asset documents. Save
                changes to retain these attachments on the ticket.
              </Typography.Paragraph>
              <FileUploader
                entry={unit}
                tags={{ ...unit.tags, unit: unit.id, 'libra-ticket': ticketId }}
                disabled={!setFieldValues}
                showExistingFiles={false}
                condensed
                onUploadComplete={onUploaded}
                onUploadError={setError}
              />
            </>
          ) : null
        }
      />
    </>
  )
}
